import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Users, Download, Mail, MoreVertical, X, CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';
import { RegOrderDrawer } from './RegOrdersPanel';
import RegistrationDetailsPanel from './RegistrationDetailsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [['registered', 'Registered'], ['waitlisted', 'Waitlist'], ['in_progress', 'In Progress'], ['canceled', 'Canceled']];
const TS_ROLES = ['company_support', 'tech_specialist', 'semprion_admin'];

// TS console — registration intake (§5.3.2) + participant profile (§5.9).
export default function RegistrationIntakePanel({ orgId, initialFid = '', userRole }) {
  const [forms, setForms] = useState([]);
  const [fid, setFid] = useState(initialFid);
  const [tab, setTab] = useState('registered');
  const [orderId, setOrderId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [exportType, setExportType] = useState('form');
  const [wlFilter, setWlFilter] = useState('');      // waitlist status filter
  const [groupFilter, setGroupFilter] = useState('');
  const offer = async (r) => {
    const message = window.prompt(r.waitlist_status === 'offered' ? 'Re-send the offer — optional message to the family' : 'Offer this spot — optional message to the family', '');
    if (message === null) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/reg-submissions/${orgId}/submissions/${r.submission_id}/waitlist-offer`, { message });
      alert(data.emailed ? 'Offer emailed' : 'Offer saved (no family email on file)'); setMenu(null); loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Could not send offer'); }
  };
  const [mail, setMail] = useState(null);   // {subject, body}
  const [overview, setOverview] = useState([]);
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [drafts, setDrafts] = useState([]);   // families who started on OL but have not submitted
  // Survey-answer columns shown in the list (per form, remembered in this browser).
  const [ansCols, setAnsCols] = useState([]);
  const [colPick, setColPick] = useState(false);
  const answerKeys = ((cols || []).find(g => g.group === 'Additional information') || {}).columns || [];
  useEffect(() => { try { setAnsCols(JSON.parse(localStorage.getItem(`ol.intake.cols.${fid}`) || '[]')); } catch { setAnsCols([]); } }, [fid]);
  const toggleCol = (k) => setAnsCols(c => { const n = c.includes(k) ? c.filter(x => x !== k) : [...c, k]; try { localStorage.setItem(`ol.intake.cols.${fid}`, JSON.stringify(n)); } catch { /* ignore */ } return n; });
  const showAnswer = (v) => Array.isArray(v) ? v.join(', ') : (v === true ? 'Yes' : v === false ? 'No' : String(v ?? '').replace(/ \(https?:[^)]+\)$/, ''));
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [menu, setMenu] = useState(null);
  const [finStatus, setFinStatus] = useState(null);   // finalize-status payload
  const [showFinalize, setShowFinalize] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const canRefund = TS_ROLES.includes(userRole);

  const loadForms = useCallback(async () => {
    if (!orgId) return;
    const { data } = await axios.get(`${API_URL}/api/reg-builder/${orgId}/forms`);
    setForms(data.forms || []);
    if ((data.forms || []).length && !fid) setFid(data.forms[0].id);
  }, [orgId, fid]);
  useEffect(() => { loadForms(); }, [loadForms]);

  const loadFinStatus = useCallback(async () => {
    if (!orgId || !fid) { setFinStatus(null); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/finalize-status`);
      setFinStatus(data);
    } catch (e) { setFinStatus(null); }
  }, [orgId, fid]);

  const loadData = useCallback(async () => {
    if (!orgId || !fid) return;
    setLoading(true);
    try {
      const [ov, subs] = await Promise.all([
        axios.get(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/overview`),
        axios.get(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/submissions?status=${tab}`),
      ]);
      setOverview(ov.data.overview || []);
      setRows(subs.data.submissions || []); setCols(subs.data.columns || []); setSel({});
      if (tab === 'in_progress') {
        const d = await axios.get(`${API_URL}/api/reg-family/${orgId}/forms/${fid}/in-progress`).catch(() => ({ data: {} }));
        setDrafts(d.data.rows || []);
      } else setDrafts([]);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [orgId, fid, tab]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadFinStatus(); }, [loadFinStatus]);

  const runFinalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/finalize`);
      setShowFinalize(false);
      await Promise.all([loadFinStatus(), loadData()]);
    } catch (e) { alert(e.response?.data?.detail || 'Finalize failed'); }
    finally { setFinalizing(false); }
  };

  const recordRefund = async (sid) => {
    if (!window.confirm('Mark this registrant as refunded and purge their personal data?')) return;
    try {
      await axios.post(`${API_URL}/api/reg-submissions/${orgId}/submissions/${sid}/record-refund`, {});
      await loadFinStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Could not record refund'); }
  };

  const act = async (path, method = 'post', body = {}) => {
    try { await axios[method](`${API_URL}/api/reg-submissions/${orgId}/${path}`, body); loadData(); }
    catch (e) { alert('Action failed'); } setMenu(null);
  };
  const bulkEmail = () => setMail({ subject: '', body: '' });
  const sendBulk = async () => {
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (!ids.length || !mail.subject.trim() || !mail.body.trim()) { alert('Subject and message are required'); return; }
    const html = mail.body.split(/\r?\n/).map(l => `<p>${l.replace(/</g, '&lt;')}</p>`).join('');
    try {
      const { data } = await axios.post(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/bulk-email`, { submission_ids: ids, subject: mail.subject.trim(), body: html });
      alert(`Sent to ${data.emailed} of ${data.recipients} famil${data.recipients === 1 ? 'y' : 'ies'}`); setMail(null);
    } catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
  };
  // R11 — export Form data / Waivers / Fee details.
  const exportCsv = () => window.open(`${API_URL}/api/reg-submissions/${orgId}/forms/${fid}/export?status=${tab}&type=${exportType}`, '_blank');
  const openProfile = async (pid) => {
    setMenu(null);
    const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/participants/${pid}`);
    setProfile(data);
  };
  const selectedCount = Object.values(sel).filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="reg-intake-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <Users className="h-5 w-5 text-amber-500" />
        <select value={fid} onChange={e => setFid(e.target.value)} className="rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="intake-form-select">
          <option value="">Select a registration…</option>
          {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        {finStatus?.finalized && (
          <span data-testid="finalized-badge"
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${finStatus.refunds_pending ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            {finStatus.refunds_pending ? 'Finalized — refunds pending' : 'Finalized'}
          </span>
        )}
        {fid && finStatus && !finStatus.finalized && (
          <button onClick={() => setShowFinalize(true)} data-testid="finalize-btn"
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Registration is final
          </button>
        )}
      </div>

      {finStatus?.finalized && (finStatus.refund_rows || []).length > 0 && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3" data-testid="refund-pending-list">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
            <AlertTriangle className="h-4 w-4" /> Refunds pending ({finStatus.refund_rows.length})
          </div>
          <div className="space-y-1">
            {finStatus.refund_rows.map(r => (
              <div key={r.submission_id} className="flex items-center justify-between text-sm bg-white dark:bg-slate-800 rounded px-3 py-2" data-testid={`refund-row-${r.submission_id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.participant}</div>
                  <div className="text-xs text-slate-500 truncate">{r.email || '—'} · ${r.fee}</div>
                </div>
                {canRefund ? (
                  <button onClick={() => recordRefund(r.submission_id)} data-testid={`record-refund-${r.submission_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500 text-amber-700 dark:text-amber-300 text-xs hover:bg-amber-100 dark:hover:bg-amber-800/40">
                    <RotateCcw className="h-3 w-3" /> Record refund
                  </button>
                ) : <span className="text-xs text-slate-400">TS records refund</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {overview.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="intake-overview">
          {overview.map(g => (
            <div key={g.group_id} className="rounded-lg border p-3 bg-white dark:bg-slate-800">
              <div className="text-sm font-semibold truncate">{g.name}</div>
              <div className="text-xs text-slate-500 mt-1">Registered {g.registered} · Cap {g.capacity ?? '—'} · WL {g.waitlist}</div>
              <div className="text-xs text-green-600">Collected ${g.collected}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 border-b dark:border-slate-700">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`intake-tab-${v}`}
            className={`px-3 py-2 text-sm ${tab === v ? 'border-b-2 border-amber-500 font-semibold' : 'text-slate-500'}`}>{l} ({rows.length && tab === v ? rows.length : ''})</button>
        ))}
        <div className="ml-auto flex gap-2 py-1">
          {tab === 'waitlisted' && (
            <select className="text-sm border rounded px-1 dark:bg-slate-800 dark:border-slate-600" value={wlFilter} onChange={e => setWlFilter(e.target.value)} data-testid="wl-filter">
              <option value="">All statuses</option><option value="waitlisted">Waitlisted</option><option value="offered">Offered</option><option value="declined">Declined</option>
            </select>
          )}
          <select className="text-sm border rounded px-1 dark:bg-slate-800 dark:border-slate-600" value={groupFilter} onChange={e => setGroupFilter(e.target.value)} data-testid="group-filter">
            <option value="">All groups</option>{[...new Set(rows.map(r => r.group).filter(Boolean))].map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {selectedCount > 0 && <button onClick={bulkEmail} className="text-sm px-2 py-1 border rounded flex items-center gap-1" data-testid="bulk-email-btn"><Mail className="h-3 w-3" />Email ({selectedCount})</button>}
          {answerKeys.length > 0 && (
            <div className="relative">
              <button onClick={() => setColPick(v => !v)} className="text-sm px-2 py-1 border rounded" data-testid="answer-cols-btn">Columns ({ansCols.length})</button>
              {colPick && (
                <div className="absolute right-0 z-20 mt-1 w-72 max-h-80 overflow-y-auto rounded border bg-white dark:bg-slate-800 dark:border-slate-600 p-2 text-sm shadow" data-testid="answer-cols">
                  <div className="text-xs text-slate-400 mb-1">Survey questions to show as columns</div>
                  {answerKeys.map(k => <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={ansCols.includes(k)} onChange={() => toggleCol(k)} />{k}</label>)}
                  <div className="flex justify-between mt-1 text-xs"><button className="underline" onClick={() => { setAnsCols(answerKeys); try { localStorage.setItem(`ol.intake.cols.${fid}`, JSON.stringify(answerKeys)); } catch { /* ignore */ } }}>All</button>
                    <button className="underline" onClick={() => { setAnsCols([]); try { localStorage.removeItem(`ol.intake.cols.${fid}`); } catch { /* ignore */ } }}>None</button>
                    <button className="underline" onClick={() => setColPick(false)}>Close</button></div>
                </div>
              )}
            </div>
          )}
          <select className="text-sm border rounded px-1 dark:bg-slate-800 dark:border-slate-600" value={exportType} onChange={e => setExportType(e.target.value)} data-testid="export-type">
            <option value="form">Form data</option><option value="waivers">Waivers</option><option value="fees">Fee details</option>
          </select>
          <button onClick={exportCsv} className="text-sm px-2 py-1 border rounded flex items-center gap-1" data-testid="export-btn"><Download className="h-3 w-3" />Export</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div> : (
        <div className="overflow-x-auto rounded-lg border dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800"><tr>
              <th className="p-2"><input type="checkbox" onChange={e => { const all = {}; rows.forEach(r => all[r.submission_id] = e.target.checked); setSel(all); }} /></th>
              <th className="p-2 text-left">Participant</th><th className="p-2 text-left">DOB</th><th className="p-2 text-left">Group</th>
              <th className="p-2 text-left">Contact</th>{ansCols.map(k => <th key={k} className="p-2 text-left whitespace-nowrap">{k}</th>)}<th className="p-2 text-left">Fees</th><th className="p-2 text-left">Payment</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No results found.</td></tr>}
              {rows.filter(r => (!wlFilter || r.waitlist_status === wlFilter) && (!groupFilter || r.group === groupFilter)).map(r => (
                <tr key={r.submission_id} className="border-t dark:border-slate-700" data-testid={`sub-row-${r.submission_id}`}>
                  <td className="p-2 text-center"><input type="checkbox" checked={!!sel[r.submission_id]} onChange={e => setSel(s => ({ ...s, [r.submission_id]: e.target.checked }))} /></td>
                  <td className="p-2"><button className="underline decoration-dotted text-left" onClick={() => setDetailId(r.submission_id)} data-testid={`sub-open-${r.submission_id}`}>{r.participant || '—'}</button>
                    {r.source === 'external' && <span className="ml-1 text-[10px] px-1 rounded bg-sky-100 text-sky-800" title="Copied from the external platform (read-only here)">external</span>}
                    {(r.shadow_diff || []).length > 0 && <span className="ml-1 text-[10px] px-1 rounded bg-red-100 text-red-700" title={(r.shadow_diff || []).join('; ')}>OL differs</span>}</td><td className="p-2 whitespace-nowrap">{r.dob}{r.dob_check && (
                    r.dob_check.status === 'match'
                      ? <span className="ml-1 text-[10px] px-1 rounded bg-emerald-100 text-emerald-800" title="Matches the birth certificate" data-testid={`dob-check-${r.submission_id}`}>✓ certificate</span>
                      : r.dob_check.status === 'mismatch'
                        ? <span className="ml-1 text-[10px] px-1 rounded bg-red-100 text-red-700" title={`Birth certificate shows ${r.dob_check.doc_dob}${r.dob_check.doc_name ? ` (${r.dob_check.doc_name})` : ''}`} data-testid={`dob-check-${r.submission_id}`}>≠ certificate {r.dob_check.doc_dob}</span>
                        : <span className="ml-1 text-[10px] px-1 rounded bg-slate-100 text-slate-600" title="The date of birth could not be read from the photo — check it by hand" data-testid={`dob-check-${r.submission_id}`}>check certificate</span>)}</td><td className="p-2">{r.group}</td>
                  <td className="p-2">{r.contact?.email}{tab === 'waitlisted' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300" data-testid={`wl-status-${r.submission_id}`}>{r.waitlist_status || 'waitlisted'}</span>}</td>{ansCols.map(k => <td key={k} className="p-2 text-xs max-w-[14rem] truncate" title={showAnswer((r.answers || {})[k])}>{showAnswer((r.answers || {})[k])}</td>)}<td className="p-2">${r.fees}{r.external_payment ? <span className="block text-[10px] text-slate-400">paid ${Number(r.external_payment.paid || 0).toFixed(2)} · bal ${Number(r.external_payment.balance || 0).toFixed(2)}</span> : null}</td><td className="p-2">{r.payment_id ? <button className="underline text-amber-400" onClick={() => setOrderId(r.payment_id)} data-testid={`intake-order-${r.submission_id}`}>{r.payment_status}</button> : r.payment_status}</td>
                  <td className="p-2 relative text-right">
                    <button onClick={() => setMenu(menu === r.submission_id ? null : r.submission_id)} data-testid={`row-menu-${r.submission_id}`}><MoreVertical className="h-4 w-4" /></button>
                    {menu === r.submission_id && (
                      <div className="absolute right-2 top-8 z-10 bg-white dark:bg-slate-800 border rounded shadow text-left w-44 dark:border-slate-600">
                        <button className="block w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => openProfile(r.player_id || r.participant)}>View profile</button>
                        <button className="block w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => { setMenu(null); setDetailId(r.submission_id); }}>Details, edit &amp; history</button>
                        {tab === 'in_progress' && <button className="block w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => act(`submissions/${r.submission_id}/nudge`)}>Send nudge</button>}
                        {tab === 'waitlisted' && r.waitlist_status !== 'declined' && <button className="block w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => offer(r)} data-testid={`wl-offer-${r.submission_id}`}>{r.waitlist_status === 'offered' ? 'Re-send offer' : 'Offer spot'}</button>}
                        {tab === 'in_progress' && <button className="block w-full text-left px-3 py-2 text-red-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => act(`submissions/${r.submission_id}`, 'delete')}>Delete</button>}
                        {tab !== 'canceled' && <button className="block w-full text-left px-3 py-2 text-red-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => act(`submissions/${r.submission_id}/cancel`)}>Cancel registration</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'in_progress' && drafts.length > 0 && (
        <div className="rounded-lg border dark:border-slate-700 overflow-x-auto" data-testid="intake-drafts">
          <div className="px-3 py-2 text-xs font-semibold border-b dark:border-slate-700">Started here, not submitted yet ({drafts.length}) <span className="font-normal text-slate-400">— saved automatically as families fill the form</span></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400"><tr><th className="p-2 text-left">Family</th><th className="p-2 text-left">Email</th><th className="p-2 text-left">Players</th><th className="p-2 text-left">Reached</th><th className="p-2 text-left">Last saved</th></tr></thead>
            <tbody>
              {drafts.map(d => (
                <tr key={d.id} className="border-t dark:border-slate-700">
                  <td className="p-2">{d.family_name || '—'}</td><td className="p-2">{d.email}</td>
                  <td className="p-2">{(d.player_names || []).join(', ') || '—'}</td>
                  <td className="p-2">{({ family: 'Parents', players: 'Players', review: 'Review & pay' })[d.step] || d.step}</td>
                  <td className="p-2 text-xs">{d.updated_at ? new Date(d.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showFinalize && finStatus && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !finalizing && setShowFinalize(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="finalize-dialog">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-amber-500" />
              <h3 className="font-bold">Mark registration as final?</h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Selected registrants are pushed into your live players &amp; families data.
              Not-selected registrants are removed (charged ones are held for a refund).
              This cannot be undone.
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border p-2 dark:border-slate-700" data-testid="finalize-count-selected">
                <div className="text-lg font-bold text-emerald-600">{finStatus.counts.selected}</div>
                <div className="text-[11px] text-slate-500">Selected</div>
              </div>
              <div className="rounded-lg border p-2 dark:border-slate-700" data-testid="finalize-count-not-selected">
                <div className="text-lg font-bold text-slate-600 dark:text-slate-300">{finStatus.counts.not_selected + finStatus.counts.pending}</div>
                <div className="text-[11px] text-slate-500">Not selected</div>
              </div>
              <div className="rounded-lg border p-2 dark:border-slate-700" data-testid="finalize-count-refund">
                <div className="text-lg font-bold text-amber-600">{finStatus.counts.refund_pending}</div>
                <div className="text-[11px] text-slate-500">Refund pending</div>
              </div>
            </div>
            {finStatus.counts.pending > 0 && (
              <p className="text-xs text-amber-600">{finStatus.counts.pending} registrant(s) still unmarked will be treated as not selected.</p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowFinalize(false)} disabled={finalizing}
                className="px-3 py-1.5 rounded-md text-sm border dark:border-slate-600" data-testid="finalize-cancel">Cancel</button>
              <button onClick={runFinalize} disabled={finalizing}
                className="px-3 py-1.5 rounded-md text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1" data-testid="finalize-confirm">
                {finalizing && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm finalize
              </button>
            </div>
          </div>
        </div>
      )}

      {profile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setProfile(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-5 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="participant-profile">
            <div className="flex justify-between items-center"><h3 className="font-bold">{profile.participant?.name}</h3><button onClick={() => setProfile(null)}><X className="h-4 w-4" /></button></div>
            <div className="text-xs text-slate-500">{profile.participant?.dob} · {profile.participant?.gender}</div>
            <div><div className="text-xs font-semibold uppercase text-slate-400">Contacts</div>
              {profile.participant?.family_id
                ? <FamilyContacts orgId={orgId} familyId={profile.participant.family_id} />
                : (profile.contacts || []).map((c, i) => <div key={i} className="text-sm">{c.name} {c.is_primary && <span className="text-xs bg-teal-600 text-white px-1 rounded">PRIMARY</span>} — {c.email}</div>)}</div>
            <div><div className="text-xs font-semibold uppercase text-slate-400">Teams</div>
              {(profile.teams || []).map((t, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span>{t.name}</span>
                  <span className="flex gap-2">
                    <button className="text-amber-600 text-xs" data-testid={`unassign-${t.slug}`} onClick={() => axios.post(`${API_URL}/api/reg-submissions/${orgId}/participants/${profile.participant.id}/unassign`, { roster_slug: t.slug }).then(() => openProfile(profile.participant.id))}>Unassign</button>
                    <button className="text-red-600 text-xs" data-testid={`remove-${t.slug}`} onClick={() => axios.post(`${API_URL}/api/reg-submissions/${orgId}/participants/${profile.participant.id}/remove`, { roster_slug: t.slug }).then(() => openProfile(profile.participant.id))}>Remove</button>
                  </span>
                </div>
              ))}</div>
            <div><div className="text-xs font-semibold uppercase text-slate-400">Registrations</div>
              {(profile.registrations || []).map(s => <div key={s.id} className="text-sm">{s.season} · {s.status} · ${s.fee}</div>)}</div>
            <div data-testid="profile-invoices"><div className="text-xs font-semibold uppercase text-slate-400">Invoices{profile.invoice_totals ? ` — billed $${profile.invoice_totals.billed} · paid $${profile.invoice_totals.paid} · balance $${profile.invoice_totals.balance}` : ''}</div>
              {(profile.invoices || []).length === 0 && <div className="text-sm text-slate-400">No invoices.</div>}
              {(profile.invoices || []).map(i => (
                <details key={i.id} className="text-sm"><summary>{i.invoice_number} · {i.title} · ${i.total} · paid ${i.paid} · <b>{i.status}</b></summary>
                  {(i.line_items || []).map((li, k) => <div key={k} className="pl-4 text-xs">{li.description} — ${li.amount}</div>)}
                  <div className="pl-4 flex gap-3 text-xs">
                    <button className="underline" onClick={async () => { await axios.post(`${API_URL}/api/invoicing/invoice/${i.id}/send-reminder`); alert('Invoice re-sent'); }}>Resend</button>
                    <button className="underline text-red-500" onClick={async () => { if (window.confirm('Delete this invoice?')) { await axios.delete(`${API_URL}/api/invoicing/invoice/${i.id}`); openProfile(profile.participant.id); } }}>Delete</button>
                  </div>
                </details>
              ))}</div>
          </div>
        </div>
      )}
      {detailId && <RegistrationDetailsPanel orgId={orgId} sid={detailId} onClose={() => setDetailId(null)} onSaved={loadData} />}
      {mail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setMail(null)} data-testid="bulk-email-modal">
          <div className="bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-lg p-4 w-full max-w-lg space-y-2" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold">Email {selectedCount} registrant famil{selectedCount === 1 ? 'y' : 'ies'}</h3>
            <input className="w-full rounded border p-2 text-sm dark:bg-slate-800 dark:border-slate-600" placeholder="Subject" value={mail.subject} onChange={e => setMail(m => ({ ...m, subject: e.target.value }))} data-testid="bulk-subject" />
            <textarea className="w-full rounded border p-2 text-sm h-40 dark:bg-slate-800 dark:border-slate-600" placeholder="Message" value={mail.body} onChange={e => setMail(m => ({ ...m, body: e.target.value }))} data-testid="bulk-body" />
            <div className="flex justify-end gap-2"><button className="px-3 py-1 text-sm" onClick={() => setMail(null)}>Cancel</button>
              <button className="px-3 py-1 rounded bg-amber-600 text-white text-sm" onClick={sendBulk} data-testid="bulk-send">Send</button></div>
          </div>
        </div>
      )}
      {orderId && <RegOrderDrawer orgId={orgId} orderId={orderId} onClose={() => { setOrderId(null); loadData(); }} />}
    </div>
  );
}


// M4 — family contacts: Primary / Secondary, promote, add, remove an unclaimed
// contact (never the last), and (re)send the account invite. /api/family-contacts.
function FamilyContacts({ orgId, familyId }) {
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState(null);
  const base = `${API_URL}/api/family-contacts/${orgId}/${familyId}`;
  const load = useCallback(async () => {
    try { const { data } = await axios.get(base); setRows(data.contacts || []); } catch { setRows([]); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  const call = async (fn) => { try { const { data } = await fn(); if (data.contacts) setRows(data.contacts); else alert('Invite sent'); } catch (e) { alert(e.response?.data?.detail || 'Failed'); } };
  return (
    <div className="space-y-1" data-testid="family-contacts">
      {rows.map((c, i) => (
        <div key={i} className="text-sm flex flex-wrap items-center gap-2">
          <span>{c.name}</span>
          <span className={`text-[10px] px-1 rounded ${c.is_primary ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-700'}`}>{c.is_primary ? 'PRIMARY' : 'SECONDARY'}</span>
          <span className="text-slate-500">{c.email}</span>
          {c.claimed ? <span className="text-[10px] text-emerald-600">account</span> : null}
          <button className="text-xs underline" onClick={() => call(() => axios.post(`${base}/role`, { email: c.email, primary: !c.is_primary }))} data-testid={`contact-role-${i}`}>{c.is_primary ? 'Make secondary' : 'Make primary'}</button>
          {!c.claimed && c.email && <button className="text-xs underline" onClick={() => call(() => axios.post(`${base}/invite`, { email: c.email }))}>Send invite</button>}
          {!c.claimed && rows.length > 1 && <button className="text-xs underline text-red-500" onClick={() => { if (window.confirm(`Remove ${c.name}?`)) call(() => axios.delete(`${base}?email=${encodeURIComponent(c.email)}`)); }} data-testid={`contact-del-${i}`}>Remove</button>}
        </div>
      ))}
      {add ? (
        <div className="flex flex-wrap gap-1">
          {['name', 'email', 'phone', 'relationship'].map(k => <input key={k} placeholder={k} className="border rounded px-1 text-sm dark:bg-slate-800" value={add[k] || ''} onChange={e => setAdd(a => ({ ...a, [k]: e.target.value }))} />)}
          <button className="text-xs underline" onClick={async () => { await call(() => axios.post(base, add)); setAdd(null); }}>Save</button>
          <button className="text-xs" onClick={() => setAdd(null)}>Cancel</button>
        </div>
      ) : <button className="text-xs underline" onClick={() => setAdd({})} data-testid="contact-add">+ Add contact</button>}
    </div>
  );
}

